/**
 * DashboardLayout — Premium executive shell
 * Wraps every authenticated page with PremiumSidebar (dark navy) + ExecutiveHeader (glass topbar).
 * Swaps in SuperAdminSidebar when a SuperAdmin is browsing the platform cockpit.
 */
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PremiumSidebar from './PremiumSidebar';
import SuperAdminSidebar from './SuperAdminSidebar';
import ExecutiveHeader from './ExecutiveHeader';

// Routes considered "SuperAdmin cockpit" pages — swap the sidebar here.
const SUPERADMIN_ROUTES = ['/admin', '/prospects'];
const isSuperAdminRoute = (pathname) =>
  SUPERADMIN_ROUTES.some((p) => pathname === p || pathname.startsWith(p + '/'));

export default function DashboardLayout({ children }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const showSuperAdminSidebar = user?.role === 'SuperAdmin' && isSuperAdminRoute(location.pathname);

  return (
    <div className="min-h-screen bg-ink-50">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-ink-900/50 lg:hidden"
          onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 z-50 h-full transform transition-transform duration-200 ease-out
        lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {showSuperAdminSidebar ? (
          <SuperAdminSidebar onClose={() => setMobileOpen(false)} onLogout={handleLogout} />
        ) : (
          <PremiumSidebar onClose={() => setMobileOpen(false)} onLogout={handleLogout} />
        )}
      </aside>

      {/* Main column */}
      <div className="lg:pl-[260px]">
        <ExecutiveHeader onMobileMenu={() => setMobileOpen(true)} />
        <main className="px-4 py-6 sm:px-6 lg:px-8" data-testid="page-main">
          {children}
        </main>
      </div>
    </div>
  );
}
